import React, { useState } from "react";
import { Link } from "react-router-dom";
import { usePoll } from "../components/PollContext";
import { Check, X, HelpCircle, Trash2, Type, Calendar, ChevronLeft, Link2, LogOut, Plus } from "lucide-react";
import {
    addPollItem as apiAddPollItem,
    castVote as apiCastVote,
    removePollItem as apiRemovePollItem,
} from "../services/pocketbaseApi";
import { inputClass } from "./PollEntry";

import DatePicker from "react-multi-date-picker";
import "react-multi-date-picker/styles/layouts/mobile.css";

const CHOICES = [
    { value: "yes", label: "Yes", icon: Check, on: "bg-polley-yes border-polley-yes text-white" },
    { value: "no", label: "No", icon: X, on: "bg-polley-no border-polley-no text-white" },
    { value: "maybe", label: "Maybe", icon: HelpCircle, on: "bg-polley-maybe border-polley-maybe text-polley-maybe-ink" },
];

const panelClass = "bg-white rounded-[22px] p-[22px] flex flex-col gap-3";

function Dialog({ title, children, onClose, align = "center" }) {
    return (
        <div
            className={`fixed inset-0 bg-polley-ink/50 flex justify-center z-50 ${align === "bottom" ? "items-end sm:items-center" : "items-center p-4"}`}
            onClick={onClose}
        >
            <div
                role="dialog"
                aria-modal="true"
                aria-label={title}
                onClick={(e) => e.stopPropagation()}
                className={`bg-white w-full p-6 shadow-lg flex flex-col gap-4 ${
                    align === "bottom" ? "max-w-screen-sm rounded-t-[28px] sm:rounded-[28px]" : "max-w-sm rounded-[28px]"
                }`}
            >
                <h2 className="m-0 font-display font-bold text-2xl">{title}</h2>
                {children}
            </div>
        </div>
    );
}

const quietButton = "h-11 px-4 rounded-full text-polley-muted font-semibold hover:text-polley-ink";
const dangerButton = "h-11 px-5 rounded-full bg-polley-no text-white font-semibold";

export default function Poll() {
    const { poll, loading, participantName, leavePoll } = usePoll();
    const [adding, setAdding] = useState(false);
    const [isDateMode, setIsDateMode] = useState(false);
    const [newItemName, setNewItemName] = useState("");
    const [inlineName, setInlineName] = useState("");
    const [selectedDates, setSelectedDates] = useState([]);
    const [error, setError] = useState("");
    const [actionLoading, setActionLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [confirmLeave, setConfirmLeave] = useState(false);
    const [confirmRemoveId, setConfirmRemoveId] = useState(null);

    if (loading) return <div className="p-8 text-center text-polley-muted">Loading poll…</div>;
    if (!poll) return <div className="p-8 text-center text-polley-no">Poll not found.</div>;

    const participants = poll.participants || [];
    const pollItems = poll.poll_items || [];
    const votes = poll.votes || [];
    const isCreator = poll.creator_name === participantName;
    const inviteUrl = `${window.location.origin}/?poll=${poll.slug}`;

    const votersFor = (itemId) => votes.find((v) => v.poll_item_id === itemId)?.voters || [];
    const getMyVote = (itemId) => votersFor(itemId).find((v) => v.participant === participantName)?.vote;
    const namesFor = (itemId, value) => votersFor(itemId).filter((v) => v.vote === value).map((v) => v.participant);
    const isAgreed = (itemId) => participants.length > 0 && namesFor(itemId, "yes").length === participants.length;
    const agreed = pollItems.filter((item) => isAgreed(item.id));
    const votedCount = pollItems.filter((item) => getMyVote(item.id)).length;

    const handleVote = async (pollItemId, value) => {
        // Tapping your current vote again clears it.
        const next = getMyVote(pollItemId) === value ? "" : value;

        const item = votes.find((v) => v.poll_item_id === pollItemId);
        if (item) {
            const me = item.voters.find((v) => v.participant === participantName);
            if (me) me.vote = next;
            else item.voters.push({ participant: participantName, vote: next });
        }

        try {
            await apiCastVote({
                poll_item: pollItemId,
                participant: participants.find((p) => p.name === participantName).id,
                value: next,
                poll_id: poll.id,
            });
        } catch (err) {
            setError(err.message);
        }
    };

    const formatDutchDate = (date) => {
        if (!date) return "";
        const days = ["Zondag","Maandag","Dinsdag","Woensdag","Donderdag","Vrijdag","Zaterdag"];
        const months = ["jan","feb","mrt","apr","mei","jun","jul","aug","sep","okt","nov","dec"];
        return `${days[date.weekDay.index]} ${date.day} ${months[date.month.index]}`;
    };

    const handleAddItem = async (e) => {
        e.preventDefault();
        setActionLoading(true);
        try {
            if (isDateMode) {
                for (const d of selectedDates) {
                    await apiAddPollItem(poll.id, { name: formatDutchDate(d) });
                }
                setSelectedDates([]);
            } else {
                if (!newItemName.trim()) return;
                await apiAddPollItem(poll.id, { name: newItemName.trim() });
                setNewItemName("");
            }
            setAdding(false);
        } catch (err) {
            setError(err.message);
        } finally {
            setActionLoading(false);
        }
    };

    const handleInlineAdd = async (e) => {
        e.preventDefault();
        if (!inlineName.trim()) return;
        try {
            await apiAddPollItem(poll.id, { name: inlineName.trim() });
            setInlineName("");
        } catch (err) {
            setError(err.message);
        }
    };

    const openAdd = (dateMode) => {
        setIsDateMode(dateMode);
        setAdding(true);
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(inviteUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            if (navigator.share) navigator.share({ title: poll.name, url: inviteUrl }).catch(() => {});
        }
    };

    return (
        <div className="pb-28 md:pb-20">
            {/* Phone header, as in the mobile board; the Layout header takes over from md up */}
            <header className="md:hidden flex items-center justify-between px-4 pt-4 pb-2">
                <Link to="/" aria-label="Back to my polls" className="w-11 h-11 rounded-[14px] bg-white flex items-center justify-center text-polley-ink">
                    <ChevronLeft size={20} />
                </Link>
                <span className="font-display font-extrabold text-xl tracking-tight">Polley</span>
                <button
                    type="button"
                    onClick={handleCopy}
                    aria-label={copied ? "Link copied" : "Copy share link"}
                    className="w-11 h-11 rounded-[14px] bg-polley-forest flex items-center justify-center text-polley-mint"
                >
                    {copied ? <Check size={20} /> : <Link2 size={20} />}
                </button>
            </header>

            <div className="px-4 sm:px-6 md:pt-4">
                <div className="max-w-[1120px] mx-auto flex flex-col gap-4 md:gap-7">
                    <section className="flex flex-wrap items-end justify-between gap-5 pt-1">
                        <div className="flex flex-col gap-2.5 min-w-0">
                            <Link to="/" className="hidden md:flex text-sm font-semibold text-polley-muted no-underline items-center gap-1.5 hover:text-polley-ink">
                                <ChevronLeft size={16} />
                                My polls
                            </Link>
                            <h1 className="m-0 font-display font-extrabold text-[40px] md:text-[56px] leading-none tracking-[-0.035em] break-words">
                                {poll.name}
                            </h1>
                            <p className="m-0 text-[15px] md:text-base text-polley-muted">
                                Asked by <strong className="text-polley-ink">{poll.creator_name}</strong>, {participants.length}{" "}
                                {participants.length === 1 ? "participant" : "participants"}
                            </p>
                        </div>
                        <div className="hidden md:flex gap-2 flex-wrap">
                            <button
                                type="button"
                                onClick={handleCopy}
                                className="h-11 px-[18px] rounded-full bg-polley-forest text-white text-[15px] font-semibold flex items-center gap-2"
                            >
                                {copied ? <Check size={18} className="text-polley-mint" /> : <Link2 size={18} className="text-polley-mint" />}
                                {copied ? "Copied" : "Copy link"}
                            </button>
                            <button
                                type="button"
                                onClick={() => setConfirmLeave(true)}
                                className="h-11 px-[18px] rounded-full border-[1.5px] border-polley-line bg-white text-[15px] font-semibold flex items-center gap-2"
                            >
                                <LogOut size={18} />
                                Leave poll
                            </button>
                        </div>
                    </section>

                    {error && (
                        <div role="alert" className="rounded-2xl bg-[#FBEDE7] text-polley-no px-4 py-3 text-sm">
                            {error}
                        </div>
                    )}

                    {/* Phone agreement banner; the desktop shows the panel in the side column */}
                    <div className="md:hidden bg-polley-forest text-white rounded-[18px] px-4 py-3.5 flex items-center gap-3">
                        <span className="w-8 h-8 flex-none rounded-[10px] bg-polley-mint flex items-center justify-center text-polley-forest">
                            <Check size={18} strokeWidth={2.4} />
                        </span>
                        <span className="text-sm leading-snug text-[#E6EEE9]">
                            {agreed.length
                                ? `Everyone agrees on ${agreed.map((i) => i.name).join(" and ")}`
                                : "No option has a yes from everyone yet"}
                        </span>
                    </div>

                    <div className="flex flex-wrap gap-6 items-start">
                        <section aria-labelledby="opts" className="flex-[999_1_560px] min-w-0 flex flex-col gap-3">
                            <div className="hidden md:flex justify-between items-baseline">
                                <h2 id="opts" className="m-0 font-display font-bold text-[22px]">Options</h2>
                                <span className="text-sm text-polley-muted">Tap to vote, tap again to clear</span>
                            </div>

                            {pollItems.length === 0 && (
                                <p className="m-0 bg-white rounded-[22px] p-5 text-polley-muted">
                                    No options yet. Add the first one, like a date or a place.
                                </p>
                            )}

                            {pollItems.map((item) => {
                                const myVote = getMyVote(item.id);
                                return (
                                    <article key={item.id} className="bg-white rounded-[20px] md:rounded-[22px] p-4 md:p-5 flex flex-col gap-3 md:gap-4">
                                        <div className="flex items-center justify-between gap-3 min-h-[24px]">
                                            <div className="flex items-center gap-3 min-w-0 flex-wrap">
                                                <h3 className="m-0 text-[17px] md:text-lg font-semibold break-words">{item.name}</h3>
                                                {isAgreed(item.id) && (
                                                    <span className="bg-polley-mint text-polley-forest text-xs font-bold px-2.5 py-1 rounded-full">
                                                        Everyone agrees
                                                    </span>
                                                )}
                                            </div>
                                            {isCreator && (
                                                <button
                                                    type="button"
                                                    onClick={() => setConfirmRemoveId(item.id)}
                                                    aria-label={`Remove option ${item.name}`}
                                                    className="w-11 h-11 -my-2.5 -mr-2 flex-none rounded-xl text-polley-muted hover:text-polley-no flex items-center justify-center"
                                                >
                                                    <Trash2 size={18} />
                                                </button>
                                            )}
                                        </div>
                                        <div role="group" aria-label={`Your vote for ${item.name}`} className="grid grid-cols-3 gap-1.5 md:gap-2">
                                            {CHOICES.map(({ value, label, icon: Icon, on }) => {
                                                const names = namesFor(item.id, value);
                                                const pressed = myVote === value;
                                                return (
                                                    <div key={value} className="flex flex-col gap-1 min-w-0">
                                                        <button
                                                            type="button"
                                                            onClick={() => handleVote(item.id, value)}
                                                            aria-pressed={pressed}
                                                            className={`h-14 md:h-[52px] rounded-[14px] border-[1.5px] px-3.5 text-[15px] font-semibold flex flex-col md:flex-row items-center justify-center md:justify-start gap-0.5 md:gap-2 ${
                                                                pressed ? on : "border-polley-line bg-white text-polley-ink hover:bg-polley-soft"
                                                            }`}
                                                        >
                                                            <Icon size={18} strokeWidth={2.4} className="hidden md:block flex-none" />
                                                            <span>{label}</span>
                                                            <span className="md:ml-auto text-xs md:text-[15px] font-medium md:font-semibold tabular-nums opacity-85">
                                                                {names.length}/{participants.length}
                                                            </span>
                                                        </button>
                                                        {names.length > 0 && (
                                                            <span className="text-xs text-polley-muted text-center break-words px-1">{names.join(", ")}</span>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </article>
                                );
                            })}

                            <form
                                onSubmit={handleInlineAdd}
                                className="hidden md:flex gap-2 bg-white rounded-[22px] p-3 border-2 border-dashed border-polley-dash"
                            >
                                <label htmlFor="newopt" className="sr-only">New option</label>
                                <input
                                    id="newopt"
                                    type="text"
                                    value={inlineName}
                                    onChange={(e) => setInlineName(e.target.value)}
                                    placeholder="Add an option, like Zaterdag 31 okt"
                                    className="flex-1 min-w-0 h-12 rounded-[14px] border-[1.5px] border-transparent px-3.5 text-base bg-polley-soft text-polley-ink placeholder:text-[#6B7A71] focus:outline-none focus:border-polley-forest"
                                />
                                <button
                                    type="button"
                                    onClick={() => openAdd(true)}
                                    className="h-12 px-4 rounded-[14px] border-[1.5px] border-polley-line bg-white text-polley-ink text-[15px] font-semibold flex items-center gap-1.5"
                                >
                                    <Calendar size={18} />
                                    Dates
                                </button>
                                <button
                                    type="submit"
                                    disabled={!inlineName.trim()}
                                    className="h-12 px-5 rounded-[14px] bg-polley-mint text-polley-forest text-[15px] font-bold flex items-center gap-1.5 disabled:opacity-60"
                                >
                                    <Plus size={18} strokeWidth={2.4} />
                                    Add
                                </button>
                            </form>
                        </section>

                        <aside className="flex-[1_1_300px] min-w-0 flex flex-col gap-3">
                            <section aria-labelledby="agree" className="hidden md:flex bg-polley-forest text-white rounded-[22px] p-[22px] flex-col gap-3">
                                <h2 id="agree" className="m-0 font-display font-bold text-xl text-polley-mint">Group agreement</h2>
                                {agreed.length > 0 ? (
                                    <ul className="list-none m-0 p-0 flex flex-col gap-2">
                                        {agreed.map((item) => (
                                            <li key={item.id} className="flex items-center gap-2.5 bg-white/[.08] rounded-[14px] px-3.5 py-3 font-semibold">
                                                <Check size={18} strokeWidth={2.4} className="text-polley-mint flex-none" />
                                                {item.name}
                                            </li>
                                        ))}
                                    </ul>
                                ) : (
                                    <p className="m-0 text-[15px] leading-normal text-polley-on-forest">
                                        No option has a yes from everyone yet. Options appear here as soon as they do.
                                    </p>
                                )}
                            </section>

                            <section aria-labelledby="people" className={panelClass}>
                                <h2 id="people" className="m-0 font-display font-bold text-xl">Participants</h2>
                                <ul className="list-none m-0 p-0 flex flex-col gap-2.5">
                                    {participants.map((p) => (
                                        <li key={p.id} className="flex items-center gap-3">
                                            <span className={`w-9 h-9 rounded-full font-bold flex items-center justify-center flex-none ${
                                                p.name === participantName ? "bg-polley-forest text-polley-mint" : "bg-polley-mint text-polley-forest"
                                            }`}>
                                                {p.name.charAt(0).toUpperCase()}
                                            </span>
                                            <span className="font-semibold truncate">
                                                {p.name}
                                                {p.name === participantName && <span className="font-normal text-polley-muted"> (you)</span>}
                                            </span>
                                            {p.name === participantName && (
                                                <span className="ml-auto text-[13px] text-polley-muted whitespace-nowrap">
                                                    {votedCount} of {pollItems.length} voted
                                                </span>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </section>

                            <section aria-labelledby="share" className={panelClass}>
                                <h2 id="share" className="m-0 font-display font-bold text-xl">Invite people</h2>
                                <p className="m-0 text-[15px] text-polley-muted leading-normal">Anyone with this link can vote and add options.</p>
                                <div className="flex items-center gap-2 bg-polley-soft rounded-[14px] p-1.5 pl-3.5">
                                    <span className="flex-1 min-w-0 text-sm truncate">{inviteUrl.replace(/^https?:\/\//, "")}</span>
                                    <button type="button" onClick={handleCopy} className="h-11 px-3.5 rounded-[10px] bg-polley-forest text-white text-sm font-semibold">
                                        {copied ? "Copied" : "Copy"}
                                    </button>
                                </div>
                            </section>

                            <button
                                type="button"
                                onClick={() => setConfirmLeave(true)}
                                className="md:hidden h-11 self-center px-4 rounded-full text-polley-muted font-semibold flex items-center gap-2"
                            >
                                <LogOut size={18} />
                                Leave poll
                            </button>
                        </aside>
                    </div>
                </div>
            </div>

            {/* Phone add bar */}
            <div className="md:hidden fixed bottom-0 inset-x-0 px-4 pt-3 pb-7 bg-white border-t border-polley-line z-40">
                <button
                    type="button"
                    onClick={() => openAdd(false)}
                    className="w-full h-14 rounded-2xl bg-polley-forest text-white font-semibold flex items-center justify-center gap-2"
                >
                    <Plus size={18} strokeWidth={2.4} className="text-polley-mint" />
                    Add an option
                </button>
            </div>

            {adding && (
                <Dialog title="Add options" onClose={() => setAdding(false)} align="bottom">
                    <form onSubmit={handleAddItem} className="flex flex-col gap-4">
                        <div className="flex gap-2" role="group" aria-label="Option type">
                            {[
                                [false, Type, "Text"],
                                [true, Calendar, "Dates"],
                            ].map(([mode, Icon, label]) => (
                                <button
                                    key={label}
                                    type="button"
                                    aria-pressed={isDateMode === mode}
                                    onClick={() => setIsDateMode(mode)}
                                    className={`h-11 px-4 rounded-full text-sm font-semibold flex items-center gap-1.5 border-[1.5px] ${
                                        isDateMode === mode ? "bg-polley-mint border-polley-mint text-polley-forest" : "bg-white border-polley-line text-polley-muted"
                                    }`}
                                >
                                    <Icon size={16} /> {label}
                                </button>
                            ))}
                        </div>

                        {isDateMode ? (
                            <>
                                <DatePicker
                                    multiple
                                    value={selectedDates}
                                    onChange={setSelectedDates}
                                    format="DD MMM YYYY"
                                    placeholder="Select one or more dates"
                                    inputClass={inputClass}
                                    className="custom-emerald-picker"
                                />
                                {selectedDates.length > 0 && (
                                    <ul className="list-none m-0 p-0 flex flex-col gap-1.5 text-sm">
                                        {selectedDates.map((d, i) => (
                                            <li key={i} className="flex justify-between items-center bg-polley-soft rounded-xl pl-3">
                                                <span>{formatDutchDate(d)}</span>
                                                <button
                                                    type="button"
                                                    aria-label={`Remove ${formatDutchDate(d)}`}
                                                    onClick={() => setSelectedDates(selectedDates.filter((_, idx) => idx !== i))}
                                                    className="w-11 h-11 flex items-center justify-center text-polley-muted hover:text-polley-no"
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </>
                        ) : (
                            <input
                                type="text"
                                aria-label="Option"
                                value={newItemName}
                                onChange={(e) => setNewItemName(e.target.value)}
                                className={inputClass}
                                placeholder="Like Zaterdag 31 okt, or the pizza place"
                                autoFocus
                            />
                        )}

                        <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => setAdding(false)} className={quietButton}>Cancel</button>
                            <button
                                type="submit"
                                disabled={actionLoading || (isDateMode ? selectedDates.length === 0 : !newItemName.trim())}
                                className="h-11 px-5 rounded-full bg-polley-forest text-white font-semibold disabled:opacity-50"
                            >
                                {actionLoading ? "Adding…" : "Add"}
                            </button>
                        </div>
                    </form>
                </Dialog>
            )}

            {confirmLeave && (
                <Dialog title="Leave poll?" onClose={() => setConfirmLeave(false)}>
                    <p className="m-0 text-[15px] text-polley-muted">
                        Your votes in this poll are removed. You can rejoin later with the link.
                    </p>
                    <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => setConfirmLeave(false)} className={quietButton}>Cancel</button>
                        <button type="button" onClick={leavePoll} className={dangerButton}>Leave</button>
                    </div>
                </Dialog>
            )}

            {confirmRemoveId && (
                <Dialog title="Remove option?" onClose={() => setConfirmRemoveId(null)}>
                    <p className="m-0 text-[15px] text-polley-muted">The option and its votes are removed for everyone.</p>
                    <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => setConfirmRemoveId(null)} className={quietButton}>Cancel</button>
                        <button
                            type="button"
                            onClick={async () => {
                                await apiRemovePollItem(poll.id, confirmRemoveId);
                                setConfirmRemoveId(null);
                            }}
                            className={dangerButton}
                        >
                            Remove
                        </button>
                    </div>
                </Dialog>
            )}
        </div>
    );
}
